import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
// Staff detail will be handled by navigating to staff table or showing inline

interface Notification {
  id: number;
  business_id: string;
  staff_id: string | null;
  type: string;
  message: string;
  data: string | object;
  priority: 'low' | 'medium' | 'high';
  read_at: string | null;
  created_at: string;
  first_name?: string;
  last_name?: string;
}

const DVLANotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dvla-staff/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        // Parse the data field for each notification
        const parsedNotifications = response.data.map((notification: Notification) => ({
          ...notification,
          data: typeof notification.data === 'string' 
            ? JSON.parse(notification.data) 
            : notification.data
        }));
        
        setNotifications(parsedNotifications);
      }
    } catch (err: any) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.error || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/dvla-staff/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read_at: new Date().toISOString() } 
            : notification
        )
      );
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/dvla-staff/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          read_at: notification.read_at || new Date().toISOString()
        }))
      );
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleViewStaff = (staffId: string) => {
    // Navigate to staff management tab
    // Note: This opens in a new tab. For better UX, you could use React Router to navigate
    // and pass the staffId as a state or query parameter
    window.location.href = `/admin/dvla`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationTypes = () => {
    const types = new Set<string>();
    notifications.forEach(notification => {
      types.add(notification.type);
    });
    return Array.from(types);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'LICENSE_STATUS_CHANGE':
        return <RefreshIcon />;
      case 'LICENSE_EXPIRING':
      case 'CARD_EXPIRING':
      case 'CATEGORY_EXPIRY':
        return <WarningIcon />;
      case 'LICENSE_EXPIRED':
      case 'CARD_EXPIRED':
        return <ErrorIcon />;
      case 'POINTS_CHANGE':
        return <ErrorIcon />;
      case 'NEW_STAFF':
        return <InfoIcon />;
      case 'CHECK_LIMIT_WARNING':
        return <WarningIcon />;
      case 'SYSTEM_UPDATE':
        return <NotificationsIcon />;
      case 'NEW_ENDORSEMENT':
        return <ErrorIcon />;
      case 'CATEGORY_CHANGE':
        return <RefreshIcon />;
      case 'LICENSE_PICTURE_CHANGE':
        return <InfoIcon />;
      case 'LOW_CREDITS':
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const renderNotificationContent = (notification: Notification) => {
    const { type, message, data } = notification;
    
    // If data is not properly parsed, use the message directly
    if (!data || typeof data !== 'object') {
      return <Typography>{message}</Typography>;
    }
    
    const dataObj = data as any;
    
    switch (type) {
      case 'LICENSE_STATUS_CHANGE':
        return (
          <Typography>
            {dataObj.old_status ? (
              <>
                <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> license status changed from{' '}
                <Chip label={dataObj.old_status} size="small" color="default" /> to{' '}
                <Chip label={dataObj.new_status} size="small" color="primary" />
              </>
            ) : (
              <>
                <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> current license status is{' '}
                <Chip label={dataObj.new_status} size="small" color="primary" />
              </>
            )}
          </Typography>
        );
        
      case 'LICENSE_EXPIRING':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> licence entitlement will expire in{' '}
            <strong>{dataObj.days_remaining} days</strong> ({dataObj.expiry_date})
          </Typography>
        );

      case 'LICENSE_EXPIRED':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> licence entitlement has expired
            {dataObj.expiry_date && ` (${dataObj.expiry_date})`}
          </Typography>
        );

      case 'CARD_EXPIRING':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> photo on the physical photocard will expire in{' '}
            <strong>{dataObj.days_remaining} days</strong> ({dataObj.expiry_date})
          </Typography>
        );

      case 'CARD_EXPIRED':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> photo on the physical photocard has expired
            {dataObj.expiry_date && ` (${dataObj.expiry_date})`}
          </Typography>
        );
        
      case 'POINTS_CHANGE':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> penalty points changed from{' '}
            <Chip label={dataObj.old_points} size="small" color={dataObj.old_points > 6 ? 'error' : dataObj.old_points > 3 ? 'warning' : 'default'} /> to{' '}
            <Chip label={dataObj.new_points} size="small" color={dataObj.new_points > 6 ? 'error' : dataObj.new_points > 3 ? 'warning' : 'default'} />
          </Typography>
        );
        
      case 'NEW_STAFF':
        return (
          <Typography>
            New driver <strong>{dataObj.staff_name}</strong> has been added to your account
          </Typography>
        );
        
      case 'CHECK_LIMIT_WARNING':
        return (
          <Typography>
            You have used <strong>{dataObj.used_checks}</strong> of your{' '}
            <strong>{dataObj.total_checks}</strong> monthly license checks
          </Typography>
        );
        
      case 'SYSTEM_UPDATE':
        return (
          <Typography>
            <strong>System Update:</strong> {dataObj.message}
          </Typography>
        );
        
      case 'INACTIVE_STAFF':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> license hasn't been checked in over 90 days
          </Typography>
        );
        
      case 'NEW_ENDORSEMENT':
        return (
          <Typography>
            New endorsement ({dataObj.endorsement_code}) with {dataObj.points} points added to{' '}
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> license
            {dataObj.offense_date && ` (offense date: ${dataObj.offense_date})`}
          </Typography>
        );
        
      case 'CATEGORY_CHANGE':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> vehicle categories have changed.
            {dataObj.changes?.added?.length > 0 && ` Added: ${dataObj.changes.added.join(', ')}.`}
            {dataObj.changes?.removed?.length > 0 && ` Removed: ${dataObj.changes.removed.join(', ')}.`}
          </Typography>
        );

      case 'CATEGORY_EXPIRY':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}</strong>{' '}
            has category-specific entitlement issues.
            {Array.isArray(dataObj.categories) && dataObj.categories.length > 0 && (
              <> Affected categories: {dataObj.categories.map((category: any) => `${category.code}${category.expired ? ' (expired)' : ` (expires in ${category.daysRemaining} days)`}`).join(', ')}.</>
            )}
          </Typography>
        );
        
      case 'LICENSE_PICTURE_CHANGE':
        return (
          <Typography>
            <strong>{dataObj.staff_name || `${notification.first_name || ''} ${notification.last_name || ''}`.trim() || 'Unknown user'}'s</strong> license has been reissued.
            {dataObj.new_issue_number && ` New issue number: ${dataObj.new_issue_number}`}
          </Typography>
        );
        
      case 'LOW_CREDITS':
        return (
          <Typography>
            {message}
            {dataObj.current_credits !== undefined && (
              <> You currently have <strong>{dataObj.current_credits}</strong> credits remaining.</>
            )}
          </Typography>
        );
        
      default:
        return <Typography>{message}</Typography>;
    }
  };

  // Filter and sort notifications
  const filteredNotifications = notifications
    .filter(notification => {
      // Apply read/unread filter
      if (filter === 'read' && !notification.read_at) return false;
      if (filter === 'unread' && notification.read_at) return false;
      
      // Apply type filter
      if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
      
      return true;
    })
    .sort((a, b) => {
      // Apply sort order
      if (sortOrder === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
    });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Notifications
          {unreadCount > 0 && (
            <Chip 
              label={unreadCount} 
              color="primary" 
              size="small" 
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchNotifications}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            sx={{ mr: 1 }}
          >
            Mark all as read
          </Button>
        </Box>
      </Box>

      {/* Alert rules callout */}
      <Alert
        severity="info"
        sx={{ mb: 3 }}
        action={
          <Button
            size="small"
            color="inherit"
            href="/admin/communications"
            sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Manage alerts
          </Button>
        }
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>Configure DVLA alert rules</Typography>
        <Typography variant="body2">
          Want to be emailed or pushed when a driver&apos;s licence expires, points increase, or status changes?
          Set up flexible DVLA alert rules under <strong>Communications → Alerts</strong>. A legacy notification
          email (if configured by your administrator) also receives summaries and immediate alerts.
        </Typography>
      </Alert>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filter}
                label="Status"
                onChange={(e) => setFilter(e.target.value as 'all' | 'unread' | 'read')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                {getNotificationTypes().map(type => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ').toLowerCase()
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortOrder}
                label="Sort"
                onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {filteredNotifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No notifications match your filters
          </Typography>
        </Paper>
      ) : (
        <Box>
          {filteredNotifications.map(notification => (
            <Card 
              key={notification.id} 
              sx={{ 
                mb: 2,
                borderLeft: notification.read_at ? '4px solid #e0e0e0' : '4px solid #1976d2',
                backgroundColor: notification.read_at ? 'background.paper' : 'action.hover'
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getNotificationIcon(notification.type)}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {notification.type.replace(/_/g, ' ').toLowerCase()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')}
                    </Typography>
                    <Chip 
                      label={notification.priority} 
                      size="small" 
                      color={getPriorityColor(notification.priority)}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(notification.created_at)}
                  </Typography>
                </Box>
                
                <Box mt={2}>
                  {renderNotificationContent(notification)}
                </Box>
              </CardContent>
              
              <CardActions>
                {notification.staff_id && (
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewStaff(notification.staff_id!)}
                  >
                    View Staff
                  </Button>
                )}
                
                {!notification.read_at && (
                  <Button
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => markAsRead(notification.id)}
                  >
                    Mark as read
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

    </Box>
  );
};

export default DVLANotifications;

